import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PaymentGatewayService } from '../../../../services/payment-gateway.service';
import { NotificationService } from 'src/app/utils/notification.service';
import { Observable } from 'rxjs';
import { AppLogoService } from 'src/app/shared/services/app-logo.service';
import { TranslationHelperService } from 'src/app/utils/translation-helper.service';

@Component({
  selector: 'app-payment-gateway-modal',
  templateUrl: './payment-gateway-modal.component.html',
  styleUrls: ['./payment-gateway-modal.component.css'],
})
export class PaymentGatewayModalComponent implements OnInit {
  gatewayForm: FormGroup;
  logoPreview: string | null = null;
  logoFile: File | null = null;
  availableGateways: {
    gatewayId: number;
    gatewayName: string;
    configurationKeys?: string[];
  }[] = [];

  logo$: Observable<string>;

  // Will be populated with configuration keys from the API
  public configurationKeys: string[] = [];

  // Track removed credentials
  private removedCredentialIds: Set<number> = new Set();
  public editTitleKey: string = $localize`:@@editPaymentGatewayTitle:Edit Payment Gateway`;
  public newTitleKey = '@@newPaymentGatewayTitle:New Payment Gateway';

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PaymentGatewayModalComponent>,
    private paymentGatewayService: PaymentGatewayService,
    private notificationService: NotificationService,
    private appLogoService: AppLogoService,
    private translateHelper: TranslationHelperService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.logo$ = this.appLogoService.logo$;
    this.gatewayForm = this.fb.group({
      paymentProvider: new FormControl('', Validators.required),
      otherPaymentGatewayName: new FormControl(''),
      contactName: new FormControl(''),
      contactEmail: new FormControl(''),
      credentials: this.fb.array([]),
      uploadLogo: new FormControl(''),
      isDefault: new FormControl(false),
    });
  }

  initForm(): void {
    this.loadAvailableGateways();

    this.gatewayForm
      .get('paymentProvider')
      ?.valueChanges.subscribe((gatewayId) => {
        this.onPaymentProviderChange(gatewayId);
      });
  }

  // Getter for credentials FormArray
  get credentials(): FormArray {
    return this.gatewayForm.get('credentials') as FormArray;
  }
  get dialogTitle(): string {
    return this.data?.isEdit ? this.editTitleKey : this.newTitleKey;
  }

  // Helper method to check if the selected gateway has configuration keys
  hasConfigurationKeys(): boolean {
    const selectedGateway = this.availableGateways.find(
      (g) => g.gatewayId === this.gatewayForm.get('paymentProvider')?.value,
    );
    return !!selectedGateway?.configurationKeys?.length;
  }

  // Helper method to check if a credential is predefined (from configuration keys)
  isPredefinedCredential(credentialName: string): boolean {
    const selectedGateway = this.availableGateways.find(
      (g) => g.gatewayId === this.gatewayForm.get('paymentProvider')?.value,
    );
    return (
      selectedGateway?.configurationKeys?.includes(credentialName) || false
    );
  }

  // Helper method to check if a credential at a specific index is predefined
  isCredentialAtIndexPredefined(index: number): boolean {
    const credential = this.credentialControls[index];
    if (!credential) return false;
    const name = credential.get('name')?.value;
    return this.isPredefinedCredential(name);
  }

  // Helper method to check if credential name should be readonly
  shouldCredentialNameBeReadonly(index: number): boolean {
    // If it's a predefined credential (from configuration keys), it should always be readonly
    if (this.isCredentialAtIndexPredefined(index)) {
      return true;
    }

    // For new credentials in edit mode, allow editing the name
    // For existing credentials in edit mode, make readonly
    if (this.data?.isEdit) {
      return !this.isNewCredential(index);
    }

    // In new mode, all credential names are editable
    return false;
  }

  // Helper method to check if a credential is newly added (not from config keys)
  isNewCredential(index: number): boolean {
    // In edit mode, check if this credential was loaded from existing configs
    if (this.data?.isEdit && this.data?.gateway?.configs) {
      const credential = this.credentialControls[index];
      if (!credential) return true;

      const name = credential.get('name')?.value;
      const value = credential.get('value')?.value;

      // Check if this credential exists in the original configs
      const existsInOriginal = this.data.gateway.configs.some(
        (config: any) =>
          config.configKey === name && config.configValue === value,
      );

      return !existsInOriginal;
    }

    // For new gateways, use the original logic
    if (!this.hasConfigurationKeys()) return true;
    return !this.isCredentialAtIndexPredefined(index);
  }

  get credentialControls(): FormGroup[] {
    return this.credentials.controls as FormGroup[];
  }

  // Method to get the selected gateway name for edit mode
  getSelectedGatewayName(): string {
    if (this.data?.isEdit && this.data?.gateway) {
      return this.data.gateway.paymentGatewayName || '';
    }

    const selectedId = this.gatewayForm.get('paymentProvider')?.value;
    const selectedGateway = this.availableGateways.find(
      (g) => g.gatewayId === selectedId,
    );
    return selectedGateway?.gatewayName || '';
  }

  // In PaymentGatewayModalComponent
  ngOnInit(): void {
    this.loadAvailableGateways();

    this.gatewayForm
      .get('paymentProvider')
      ?.valueChanges.subscribe((gatewayId) => {
        // Only trigger change handler if NOT in edit mode
        if (!this.data?.isEdit) {
          this.onPaymentProviderChange(gatewayId);
        }
      });
  }

  private onPaymentProviderChange(gatewayId: number): void {
    // Clear existing credentials
    while (this.credentials.length !== 0) {
      this.credentials.removeAt(0);
    }

    // Don't populate if it's in edit mode (let populateForm handle it)
    if (this.data?.isEdit && this.data?.gateway) {
      return;
    }

    // Find the selected gateway
    const selectedGateway = this.availableGateways.find(
      (g) => g.gatewayId === gatewayId,
    );

    // If gateway has configuration keys, add them
    if (selectedGateway?.configurationKeys?.length) {
      selectedGateway.configurationKeys.forEach((key: string) => {
        this.addCredential(key, '');
      });
    } else {
      // For 'Others' or gateways without config keys, add one empty credential
      this.addCredential();
    }

    // Show/hide "Other Payment Gateway Name" field
    const otherNameControl = this.gatewayForm.get('otherPaymentGatewayName');
    if (gatewayId === 0) {
      otherNameControl?.setValidators(Validators.required);
    } else {
      otherNameControl?.clearValidators();
    }
    otherNameControl?.updateValueAndValidity();
  }

  private populateForm(gateway: any): void {
    // Clear existing credentials
    while (this.credentials.length !== 0) {
      this.credentials.removeAt(0);
    }

    // Set the paymentProvider value
    this.gatewayForm.patchValue({
      paymentProvider: gateway.paymentProvider,
      contactName: gateway.contactName || '',
      contactEmail: gateway.contactEmail || '',
      isDefault: gateway.isDefault || false,
      uploadLogo: gateway.paymentGatewayLogo || '',
    });

    // Then populate credentials from saved configs
    if (gateway.configs && gateway.configs.length > 0) {
      gateway.configs.forEach((config: any) => {
        this.addCredential(config.configKey, config.configValue, config.id);
      });
    } else {
      // Fallback if no configs exist
      const selectedGateway = this.availableGateways.find(
        (g) => g.gatewayId === gateway.paymentProvider,
      );

      if (selectedGateway?.configurationKeys?.length) {
        selectedGateway.configurationKeys.forEach((key: string) =>
          this.addCredential(key, ''),
        );
      } else {
        this.addCredential();
      }
    }

    // Set logo preview if available
    if (gateway.paymentGatewayLogo) {
      this.logoPreview = gateway.paymentGatewayLogo;
    }
  }

  addCredential(name: string = '', value: string = '', id?: number): void {
    const credGroup = this.fb.group({
      id: id || null,
      name: [name, Validators.required],
      value: [value, Validators.required],
    });
    this.credentials.push(credGroup);
  }

  removeCredential(index: number): void {
    const credential = this.credentials.at(index);
    if (credential && credential.value) {
      // If this credential has an ID (from existing data), track it as removed
      const credValue = credential.value;
      if (credValue && credValue.id) {
        this.removedCredentialIds.add(credValue.id);
      }
    }

    this.credentials.removeAt(index);
    // If this was the last credential, add a new empty one
    if (this.credentials.length === 0) {
      this.addCredential();
    }
  }

  public onFileChange(fileData: string, formCtrl: string): void {
    if (!fileData) {
      this.gatewayForm.get(formCtrl)?.setValue('');
      this.logoPreview = null;
      this.logoFile = null;
      return;
    }

    this.logoPreview = fileData;
    this.logoFile = null;
    this.gatewayForm.get(formCtrl)?.setValue(fileData);
  }

  isValid(): boolean {
    // Just check if the form is valid
    // All fields are optional now, so we only need to check the form's validity
    return this.gatewayForm.valid;
  }

  save(): void {
    if (!this.isValid()) return;

    const formValue = this.gatewayForm.value;

    // Find the selected gateway to get its name
    const selectedGateway = this.availableGateways.find(
      (g) => g.gatewayId === formValue.paymentProvider,
    );

    const configList = [
      // Add current credentials
      ...formValue.credentials.map((cred: any) => ({
        id: cred.id || 0,
        configKey: cred.name,
        configValue: cred.value || '',
        remove: false,
      })),
      // Add removed credentials
      ...Array.from(this.removedCredentialIds).map((id) => ({
        id: id,
        configKey: '',
        configValue: '',
        remove: true,
      })),
    ];

    const payload = {
      paymentGatewayName: this.data?.isEdit
        ? this.data.gateway.paymentGatewayName ||
          selectedGateway?.gatewayName ||
          formValue.otherPaymentGatewayName ||
          ''
        : selectedGateway?.gatewayName ||
          formValue.otherPaymentGatewayName ||
          '',
      paymentGatewayLogo: formValue.uploadLogo || '',
      contactEmail: formValue.contactEmail || '',
      contactName: formValue.contactName || '',
      isDefault: !!formValue.isDefault,
      configs: configList,
      otherPaymentGatewayName: formValue.otherPaymentGatewayName || '',
      incomingGatewayId: formValue.paymentProvider,
    };

    // Use different endpoints for create vs update
    const serviceCall = this.data?.isEdit
      ? this.paymentGatewayService.updateGatewayById(
          this.data.gateway.id,
          payload,
        )
      : this.paymentGatewayService.configurePaymentGateway(payload);

    serviceCall.subscribe({
      next: () => {
        const isOthers = formValue.paymentProvider === 0;
        const successMessage = isOthers
          ? `Your custom payment gateway "${formValue.otherPaymentGatewayName}" has been submitted for review. Please allow up to 24 hours for processing.`
          : this.data?.isEdit
            ? 'Payment gateway updated successfully'
            : 'Payment gateway configured successfully. Changes will be applied within 24 hours.';

        this.dialogRef.close(this.translateHelper.translateBackendMessage(successMessage));
      },
      error: (error) => {
        this.notificationService.error(
         this.translateHelper.translateBackendMessage(error.error?.message || 'Failed to configure payment gateway'),
        );
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private loadAvailableGateways(): void {
    const countryCode = this.data?.countryCode || 'NGA';

    this.paymentGatewayService.getCountryGateways(countryCode).subscribe({
      next: (response: any) => {
        const gateways = Array.isArray(response)
          ? response
          : response?.data || [];

        this.availableGateways = gateways.map((gateway: any) => ({
          gatewayId: gateway.gatewayId,
          gatewayName: gateway.gatewayName,
          configurationKeys: gateway.configurationKeys || [],
        }));

        this.availableGateways.push({
          gatewayId: 0,
          gatewayName: 'Others',
        });

        // Populate form in edit mode after gateways are loaded
        if (this.data?.isEdit && this.data?.gateway) {
          this.populateForm(this.data.gateway);
        }
      },
      error: () => {
        this.availableGateways = [
          {
            gatewayId: 0,
            gatewayName: 'Others',
          },
        ];

        // Still populate form even if gateway loading fails
        if (this.data?.isEdit && this.data?.gateway) {
          this.populateForm(this.data.gateway);
        }
      },
    });
  }
}
